import { appendIf, isEmailValid, isFunction } from '@btime/helpers'
import { assoc, compose, pick } from 'ramda'
import { gql, useMutation } from '@apollo/client'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'

import { accountsClient } from '../../../../utils/apollo/api'
import { cleanFieldValuesRequest, validateFieldValues } from '../../../../utils/helpers/fields'
import { useCloseModalOrShowMessage, useModalData } from '../../../../components/modals/ModalsContext'
import * as tracker from '@btime/tracker'
import { isEmpty } from 'lodash'
import { formatApiTime } from '../../../../utils/helpers/date'
import { useReloadLoggedUser } from '../../../../utils/session/reloadLoggedUser'

const MUTATION = gql`
  mutation UpsertUser($input: UserInput) {
    upsertUser(input: $input) {
      id
      name
    }
  }
`

const cleanWorkShifts = workShifts => {
  return workShifts.map(workShift => {
    return workShift.map(workShift => {
      return {
        start: formatApiTime(workShift.start),
        end: formatApiTime(workShift.end)
      }
    })
  })
}

const cleanContract = compose(
  pick(['id', 'groupId', 'contractGroupIds', 'profileId', 'fieldValues', 'workShifts', 'relatedUsers']),
  item => assoc('id', item?.id, item),
  item => assoc('groupId', Array.isArray(item.groups) ? null : item.group?.id ?? null, item),
  item =>
    assoc(
      'contractGroupIds',
      Array.isArray(item.groups) && item.groups.length ? item.groups.map(group => group.id) : null,
      item
    ),
  item => assoc('profileId', item.profile?.id, item),
  item =>
    assoc(
      'relatedUsers',
      item.relatedUsers?.map(user => (user?.contract?.id ? user.contract.id : user.contractId)),
      item
    ),
  item => assoc('fieldValues', cleanFieldValuesRequest(item.fieldValues || []), item),
  item => assoc('workShifts', JSON.stringify(cleanWorkShifts(item.workShifts || [])), item)
)

const cleanRequest = compose(
  pick(['id', 'name', 'email', 'contract', 'fieldValues', 'canAccessPaymentModule', 'isFakeEmail']), //
  item => assoc('contract', cleanContract(item.contract || {}), item)
)

const validate = (form, requireUserGroup, setErrors, t) => {
  const errors = [
    ...appendIf(!form.name, { path: ['name'], message: t('validations:required') }),
    ...appendIf(form.name?.length > 60, { path: ['name'], message: t('maxLengthExceeded') }),
    ...appendIf(!isEmailValid(form.email), { path: ['email'], message: t('validations:invalidEmail') }),
    ...appendIf(!form.email, { path: ['email'], message: t('validations:required') }),
    ...appendIf(!form.contract?.profile?.id, {
      path: ['contract', 'profile'],
      message: t('validations:required')
    }),
    ...appendIf(
      !Array.isArray(form.contract?.group) &&
        !form.contract?.group?.id &&
        requireUserGroup &&
        !Array.isArray(form.contract?.groups) &&
        !form.contract?.groups?.length,
      {
        path: ['contract', 'group'],
        message: t('validations:required')
      }
    ),
    ...appendIf(Array.isArray(form.contract?.groups) && !form.contract?.groups?.length && requireUserGroup, {
      path: ['contract', 'groups'],
      message: t('validations:required')
    }),

    ...validateFieldValues(form, ['contract', 'fieldValues']),
    ...validateWorkShifts(form, false, t)
  ]

  setErrors(errors)
  return errors.length === 0
}

export const validateWorkShifts = (form, validateOnlyWorkShifts, t) => {
  let errors = []

  if (!form.contract?.workShifts) return []

  errors = form.contract?.workShifts?.reduce((errors, item, weekDayIndex) => {
    let innerErrors = []

    item.forEach((innerItem, weekdayValuesIndex) => {
      innerErrors = [
        ...innerErrors,
        ...appendIf(!isEmpty(innerItem) && !innerItem.start, {
          path: ['contract', 'workShifts', weekDayIndex, weekdayValuesIndex, 'start'],
          message: t('validations:required')
        }),
        ...appendIf(!isEmpty(innerItem) && !innerItem.end, {
          path: ['contract', 'workShifts', weekDayIndex, weekdayValuesIndex, 'end'],
          message: t('validations:required')
        })
      ]
    })

    return [...errors, ...innerErrors]
  }, [])

  if (!validateOnlyWorkShifts && errors.length > 0) {
    errors = [...errors, { path: ['contract', 'workShifts'], message: t('checkWorkShifts') }]
  }

  return errors
}

export const useSaveUser = () => {
  const { t } = useTranslation('editUser')
  const tValidations = useTranslation('validations').t
  const requireUserGroup = useSessionPermission('requireUserGroup')
  const closeModalOrShowMessage = useCloseModalOrShowMessage()
  const reload = useReloadLoggedUser()

  const { onSave } = useModalData() || {}
  const [request, state] = useMutation(MUTATION, { refetchQueries: ['Users'], client: accountsClient })

  const handleRequest = (form, actions) => {
    const input = cleanRequest(form)
    const { setErrors } = actions
    const tCombined = key => {
      if (key.startsWith('validations:')) {
        return tValidations(key.replace('validations:', ''))
      }
      return t(key)
    }
    const isValid = validate(form, requireUserGroup, setErrors, tCombined)

    if (isValid) {
      return request({ variables: { input } }).then(resp => {
        if (!resp.errors) {
          if (isFunction(onSave)) onSave(resp.data?.upsertUser)
          closeModalOrShowMessage(t('successTitle'), t('successMessage'))
          reload()
          tracker.signUp([input])
        }
      })
    }
  }

  return [handleRequest, state]
}
